import {useMemo, useState} from 'react';

import {useDispatch} from 'react-redux';

import {
  Avatar,
  Box,
  Center,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';

import {SearchIcon} from '@chakra-ui/icons';
import {Link as RouterLink, useLocation} from 'react-router-dom';

import {useOrganizations} from '../hooks/useOrganizations';
import {addAssignKpiFilter} from '../slices/assignKpiFilterSlice';
import {addBillFilter} from '../slices/billFilterSlice';
import {addCalendarFilter} from '../slices/calendarFilterSlice';
import {addTourFilter} from '../slices/tourFilterSlice';
import {addTours} from '../slices/toursSlice';
import {Organization} from '../types/organizations';
import {Card} from './Card';

interface IOrganizationPickerListProps {
  onClose?: () => void;
}

type OrgFilterState = 'all' | 'active' | 'inactive';

const FILTER_TABS: {key: OrgFilterState; label: string; color: string}[] = [
  {key: 'active', label: 'Activas', color: 'teal'},
  {key: 'inactive', label: 'Inactivas', color: 'red'},
  {key: 'all', label: 'Todas', color: 'darkBlue'},
];

export function OrganizationPickerList(props: IOrganizationPickerListProps) {
  const {onClose} = props;
  const organizations = useOrganizations();
  const dispatch = useDispatch();
  const {pathname} = useLocation();
  const [searchString, setSearchString] = useState<string>('');
  const pathSuffix =
    pathname !== '/' ? pathname.split('/').slice(2).join('/') : 'home';
  const [orgFilter, setOrgFilter] = useState<OrgFilterState>('active');

  const filteredOrganizations = useMemo<Organization[]>(() => {
    return organizations.data
      .filter((it: Organization) => {
        if (orgFilter === 'all') return true;
        if (orgFilter === 'active') return it.enabled;
        return !it.enabled;
      })
      .filter((org: Organization) =>
        org.name.toLowerCase().includes(searchString.toLowerCase()),
      );
  }, [organizations.data, orgFilter, searchString]);

  const clearInputs = () => {
    dispatch(addTours([]));
    dispatch(addTourFilter([]));
    dispatch(addBillFilter([]));
    dispatch(addCalendarFilter([]));
    dispatch(addAssignKpiFilter([]));
    onClose?.();
  };

  if (organizations.isLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  return (
    <VStack mt={5} w='100%' spacing={3}>
      <Box w='100%' px={5}>
        <InputGroup>
          <InputLeftElement pointerEvents='none'>
            <SearchIcon />
          </InputLeftElement>
          <Input
            variant='outline'
            value={searchString}
            placeholder='Buscar organización'
            onChange={evt => setSearchString(evt.target.value)}
          />
        </InputGroup>
      </Box>

      <Box w='100%' px={5}>
        <Tabs
          variant='unstyled'
          isFitted
          index={FILTER_TABS.findIndex(t => t.key === orgFilter)}
          onChange={i => setOrgFilter(FILTER_TABS[i].key)}>
          <TabList
            aria-label='Filtro de organizaciones'
            border='1px solid'
            borderColor='gray.200'
            borderRadius='full'
            overflow='hidden'
            transition='box-shadow 0.15s'
            sx={{
              '&:has(:focus-visible)': {
                boxShadow: 'outline',
              },
            }}>
            {FILTER_TABS.map(tab => (
              <Tab
                key={tab.key}
                py={1.5}
                fontSize='sm'
                fontWeight='semibold'
                color='gray.700'
                transition='all 0.2s'
                _hover={{
                  bg: `${tab.color}.100`,
                  color: `${tab.color}.800`,
                }}
                _selected={{
                  bg: `${tab.color}.500`,
                  color: 'white',
                }}
                _focus={{boxShadow: 'none'}}>
                {tab.label}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      </Box>

      <VStack
        maxHeight={{base: '50vh', md: '430px'}}
        w='100%'
        overflowY='auto'
        p={5}>
        {filteredOrganizations.map((org: Organization) => (
          <Card
            as={RouterLink}
            onClick={clearInputs}
            justifyContent='left'
            key={org.idOrganization}
            padding='36px 20px'
            to={`/${org.idOrganization}/${pathSuffix}`}>
            <HStack justifyContent='flex-start' minW={0} w='100%' spacing={4}>
              <Avatar name={org.name} />
              <Text noOfLines={2} lineHeight='1.3' whiteSpace='normal'>
                {org.name}
              </Text>
            </HStack>
          </Card>
        ))}
      </VStack>
    </VStack>
  );
}
